// The host's half of the seam for the kit's sync engine (mt2 runtimes/ts/host): the shared driver (./driver.ts) drains
// what the core asks for; the handlers here answer it with this runtime's fetch, WebSocket, timers and storage. Holds
// no rule: policy (what to read, when to reconnect, what a status means) is the core's. Works with any `Core`: the
// wasm binding or the C ABI one.
import { Driver } from "./driver";
import type { AppError, Ask, Core, Effect, Io, Mutation, Outcome, Query, Row, Storage, View, Watched } from "./types";

export type KitOptions = {
	/** The instance's origin, e.g. `https://hq.mml00-work.workers.dev`. Paths come from the core. */
	origin: string;
	/** Socket origin; defaults to the origin with ws(s)://. */
	ws?: string;
	token?: string | null;
	/** Where records live between launches. Absent: memory only. */
	storage?: Storage;
	/** Dial the live socket (default true). false: catch up with `pull()` over HTTP. */
	live?: boolean;
	/** Kinds known up front, as `GET /graphql/kinds` serves them (skips reading them before the first read). */
	kinds?: unknown[];
	/** Local-first: a replica of everything the person may see, built once from the log (`/sync`, in pages) and kept
	 *  current by the sockets; every query is answered from it, none is fetched. */
	replica?: boolean;
	/** The signed-in person's id at the home (features read it); `setPerson` changes it. */
	person?: string | null;
	/** Paths, if the instance mounts them elsewhere. */
	paths?: { graphql?: string; live?: string; sync?: string };
	/** fetch to use (tests, Workers service bindings). */
	fetch?: typeof fetch;
	/** WebSocket constructor to use. */
	WebSocket?: typeof WebSocket;
	onWarn?: (message: string) => void;
};

function seed(): number {
	const a = crypto.getRandomValues(new Uint32Array(2));
	// 53 bits: a JSON number survives the trip into the core's u64 exactly.
	return (a[0]! & 0x1fffff) * 0x100000000 + a[1]!;
}

export type WasmSource = URL | string | BufferSource | WebAssembly.Module;
/** A wasm-bindgen package's loader, run once: `wasm` a URL, bytes or a compiled module (Workers: the imported module);
 *  default: beside the package. Every binding of the sync engine (the kit's own, an assembly) loads this way. */
export function loader(init: (o?: { module_or_path: URL }) => Promise<unknown>, initSync: (o: { module: BufferSource }) => unknown) {
	let ready: Promise<unknown> | null = null;
	return (wasm?: WasmSource): Promise<unknown> => {
		if (wasm instanceof WebAssembly.Module || ArrayBuffer.isView(wasm) || wasm instanceof ArrayBuffer) ready ??= Promise.resolve(initSync({ module: wasm as BufferSource }));
		return (ready ??= init(wasm ? { module_or_path: wasm as URL } : undefined));
	};
}

/** The engine's `Options` JSON for these kit options (a fresh seed each time): what any binding of the sync engine
 *  (the kit's own, an assembly with features) is constructed with. */
export function engineOptions(o: KitOptions): string {
	return JSON.stringify({
		token: o.token ?? null,
		seed: seed(),
		storage: !!o.storage,
		live: o.live ?? true,
		kinds: o.kinds ?? null,
		replica: o.replica ?? false,
		person: o.person ?? null,
		paths: { graphql: "/graphql", live: "/live", sync: "/sync", ...(o.paths ?? {}) },
	});
}

export type Status = { live: "off" | "connecting" | "live"; since: number | null; pending: number };

export type Subscription = {
	/** Re-declare the query (a wider window, another filter). */
	update(query: Query): void;
	close(): void;
};

export type Written = { id: string; tx: string; done: Promise<Outcome> };

export type Watching = { update(params: unknown): void; close(): void };

export type Acted<T> = { reply: T; writes: Written[]; done: Promise<Outcome | null> };

/** A refused or failed `ask`/`read`, carrying the core's error. */
export class KitError extends Error {
	constructor(readonly error: AppError) {
		super("message" in error && error.message ? error.message : error.type === "refused" ? error.code : error.type.replace("_", " "));
		this.name = "KitError";
	}
}

export interface Kit {
	/** Declare a need; `cb` gets the view now and on every change. */
	subscribe(query: Query, cb: (view: View) => void): Subscription;
	/** The view of a query as this device holds it now (no fetch). */
	select(query: Query): View;
	/** A query's rows once they are ready (fetched if this device has not read them yet); rejects with a KitError. */
	read(query: Query): Promise<Row[]>;
	/** A one-shot question (me, session, usage, kinds, search, record, detail, history, install); rejects with a KitError. */
	ask<T = unknown>(ask: Ask): Promise<T>;
	/** Declare a feature's view (`board.snapshot`, `chat.overview`: the features the core's assembly registers); `cb`
	 *  gets `{data, status, error}` now and on every change. `update(params)` re-declares it. */
	watch<T = unknown>(view: string, params: unknown, cb: (v: Watched<T>) => void): Watching;
	/** A feature's view once, as this device holds it now. */
	look<T = unknown>(view: string, params?: unknown): Watched<T>;
	/** A feature's intent (`chat.send`, `board.place`): its writes show at once like `mutate`'s; `done` resolves with
	 *  the first refusal, else when every write landed. Throws a KitError when the intent itself is refused. */
	act<T = unknown>(act: string, params?: unknown): Acted<T>;
	/** Who is signed in (the person's id at the home): features read it (what is mine, what is unread). */
	setPerson(person: string | null): void;
	/** An optimistic write: shows in every view at once, rolls back if refused. */
	mutate(m: Mutation): Written;
	create(kind: string, fields: Record<string, unknown>, opts?: { id?: string; prefix?: string; space?: string }): Written;
	update(kind: string, id: string, patch: Record<string, unknown>): Written;
	setToken(token: string | null): void;
	/** Try again what failed (reads, queued writes, the socket). */
	retry(): void;
	/** HTTP catch-up (for `live: false`). */
	pull(): void;
	/** Forget everything on this device (sign-out). */
	reset(): void;
	readonly status: Status;
	onStatus(cb: (s: Status) => void): () => void;
	/** Records of these kinds changed from the log (live or catch-up): a screen that read them once may read again. */
	onChange(cb: (kinds: string[]) => void): () => void;
	/** Called when the credential stops working: refresh it with `setToken`, or sign out. */
	onUnauthorized(cb: () => void): () => void;
	close(): void;
}

export function host(core: Core, o: KitOptions): Kit {
	const origin = o.origin.replace(/\/$/, "");
	const wsOrigin = (o.ws ?? origin.replace(/^http/, "ws")).replace(/\/$/, "");
	const doFetch = o.fetch ?? fetch.bind(globalThis);
	const WS = o.WebSocket ?? globalThis.WebSocket;
	const warn = o.onWarn ?? ((m: string) => console.warn(`[kit] ${m}`));
	const views = new Map<number, (v: any) => void>();
	const settle = new Map<string, (o: Outcome) => void>();
	const early = new Map<string, Outcome>();
	const answers = new Map<number, (e: Extract<Effect, { type: "answered" }>) => void>();
	const earlyAnswers = new Map<number, Extract<Effect, { type: "answered" }>>();
	const statusCbs = new Set<(s: Status) => void>();
	const unauthCbs = new Set<() => void>();
	const changeCbs = new Set<(kinds: string[]) => void>();
	const timers = new Set<ReturnType<typeof setTimeout>>();
	let status: Status = { live: "off", since: null, pending: 0 };
	let socket: WebSocket | null = null;
	/** Connected instances' sockets (absolute wss:// paths), by their open id. */
	const remotes = new Map<number, WebSocket>();
	let closed = false;

	// The sync engine answers by its own inputs (http_done, timer, storage_loaded, frames): no handler returns a promise.
	type Of<K extends Io["io"]> = Extract<Io, { io: K }>;
	const driver = new Driver(
		core,
		{
			view: (item: Of<"view">) => void views.get(item.sub)?.(item.view),
			effect: (item: Of<"effect">) => effect(item.effect),
			http: (item: Of<"http">) => void request(item),
			timer: (item: Of<"timer">) => {
				const t = setTimeout(() => {
					timers.delete(t);
					call({ in: "timer", id: item.id });
				}, item.ms);
				timers.add(t);
			},
			socket_open: (item: Of<"socket_open">) => {
				if (/^wss?:\/\//.test(item.path)) dialRemote(item.id, item.path, item.protocols);
				// a shard's log or the summary channel at the home (hq:Decision/storage-per-space): a socket of its own,
				// its frames tagged with its id like a Connected instance's; only the instance log's is the home socket
				else if (/[?&](log|summary)=/.test(item.path)) dialRemote(item.id, `${wsOrigin}${item.path}`, item.protocols);
				else dial(item.id, item.path, item.protocols);
			},
			socket_send: (item: Of<"socket_send">) => {
				const s = item.id == null ? socket : remotes.get(item.id);
				if (s?.readyState === 1) s.send(item.text);
			},
			socket_close: (item: Of<"socket_close">) => {
				if (item.id == null) hangUp();
				else hangUpRemote(item.id);
			},
			storage_load: (item: Of<"storage_load">) => {
				if (!o.storage) {
					call({ in: "storage_failed", id: item.id, reason: "no storage" });
					return;
				}
				o.storage.load().then(
					(stored) => call({ in: "storage_loaded", id: item.id, stored }),
					(e) => call({ in: "storage_failed", id: item.id, reason: String(e?.message ?? e) }),
				);
			},
			storage_apply: (item: Of<"storage_apply">) => {
				o.storage?.apply(item.writes).catch((e) => warn(`storage: ${e?.message ?? e}`));
			},
		},
		warn,
	);
	const call = (input: object, before?: (reply: any) => void): any => driver.call(input, before);

	function effect(e: Effect) {
		if (e.type === "settled") {
			const cb = settle.get(e.tx);
			if (cb) {
				settle.delete(e.tx);
				cb(e.outcome);
			} else early.set(e.tx, e.outcome);
		} else if (e.type === "status") {
			status = { live: e.live, since: e.since, pending: e.pending };
			for (const cb of statusCbs) cb(status);
		} else if (e.type === "answered") {
			const cb = answers.get(e.ask);
			if (cb) {
				answers.delete(e.ask);
				cb(e);
			} else earlyAnswers.set(e.ask, e);
		} else if (e.type === "changed") for (const cb of changeCbs) cb(e.kinds);
		else if (e.type === "unauthorized") for (const cb of unauthCbs) cb();
		else warn(e.message);
	}

	async function request(item: Extract<Io, { io: "http" }>) {
		try {
			const r = await doFetch(`${origin}${item.path}`, {
				method: item.method,
				headers: Object.fromEntries(item.headers),
				body: item.body ?? undefined,
			});
			const body = await r.text();
			call({ in: "http_done", id: item.id, status: r.status, body });
		} catch (e) {
			call({ in: "http_failed", id: item.id, reason: String((e as Error)?.message ?? e) });
		}
	}

	function dial(id: number, path: string, protocols: string[]) {
		hangUp();
		let s: WebSocket;
		try {
			s = new WS(`${wsOrigin}${path}`, protocols);
		} catch (e) {
			queueMicrotask(() => call({ in: "socket_closed", id, reason: String(e) }));
			return;
		}
		socket = s;
		s.onopen = () => socket === s && call({ in: "socket_open", id });
		s.onmessage = (ev) => socket === s && call({ in: "frame", text: String(ev.data) });
		s.onclose = (ev) => {
			if (socket !== s) return;
			socket = null;
			call({ in: "socket_closed", id, reason: ev.reason || `closed (${ev.code})` });
		};
	}

	/** A Connected instance's socket (truss:Decision/live-remote-ticket): the ticket rides as the second subprotocol. */
	function dialRemote(id: number, url: string, protocols: string[]) {
		hangUpRemote(id);
		let s: WebSocket;
		try {
			s = new WS(url, protocols);
		} catch (e) {
			queueMicrotask(() => call({ in: "socket_closed", id, reason: String(e) }));
			return;
		}
		remotes.set(id, s);
		const mine = () => remotes.get(id) === s;
		s.onopen = () => mine() && call({ in: "socket_open", id });
		s.onmessage = (ev) => mine() && call({ in: "frame", text: String(ev.data), id });
		s.onclose = (ev) => {
			if (!mine()) return;
			remotes.delete(id);
			call({ in: "socket_closed", id, reason: ev.reason || `closed (${ev.code})` });
		};
	}

	function hangUpRemote(id: number) {
		const s = remotes.get(id);
		remotes.delete(id);
		if (s) {
			s.onclose = null;
			s.close(1000);
		}
	}

	function hangUp() {
		const s = socket;
		socket = null;
		if (s) {
			s.onclose = null;
			s.close(1000);
		}
	}

	// Back to the tab or the network: do not wait for a backoff (a phone drops sockets while asleep).
	const wake = () => typeof document !== "undefined" && document.visibilityState === "visible" && call({ in: "wake", online: true });
	const online = () => call({ in: "wake", online: true });
	const offline = () => call({ in: "wake", online: false });
	if (typeof window !== "undefined" && typeof document !== "undefined") {
		document.addEventListener("visibilitychange", wake);
		window.addEventListener("online", online);
		window.addEventListener("offline", offline);
	}

	/** A write's outcome (it may have settled in the drain of the call that queued it). */
	const outcome = (tx: string) =>
		new Promise<Outcome>((ok) => {
			const now = early.get(tx);
			if (now) {
				early.delete(tx);
				ok(now);
			} else settle.set(tx, ok);
		});
	const mutate = (m: Mutation): Written => {
		const { tx, id } = call({ in: "mutate", mutation: m }) as { tx: string; id: string };
		return { id, tx, done: outcome(tx) };
	};

	const kit: Kit = {
		subscribe(query, cb) {
			// The first view is in the drain of this very call: register the callback before draining.
			const { sub } = call({ in: "subscribe", query }, (r: { sub: number }) => views.set(r.sub, cb)) as { sub: number };
			return {
				update: (q) => call({ in: "resubscribe", sub, query: q }),
				close: () => {
					views.delete(sub);
					call({ in: "unsubscribe", sub });
				},
			};
		},
		select: (query) => call({ in: "select", query }) as View,
		watch<T>(view: string, params: unknown, cb: (v: Watched<T>) => void): Watching {
			const open = (p: unknown) =>
				(call({ in: "watch", view, params: p ?? null }, (r: { sub: number }) => views.set(r.sub, cb as (v: unknown) => void)) as { sub: number }).sub;
			let sub = open(params);
			return {
				update(p) {
					const old = sub;
					sub = open(p);
					views.delete(old);
					call({ in: "unsubscribe", sub: old });
				},
				close() {
					views.delete(sub);
					call({ in: "unsubscribe", sub });
				},
			};
		},
		look: <T>(view: string, params?: unknown) => call({ in: "look", view, params: params ?? null }) as Watched<T>,
		act<T>(act: string, params?: unknown): Acted<T> {
			const r = call({ in: "act", act, params: params ?? null }) as { reply?: T; writes?: { tx: string; id: string }[]; error?: AppError };
			if (r.error) throw new KitError(r.error);
			const writes = (r.writes ?? []).map(({ tx, id }) => ({ tx, id, done: outcome(tx) }));
			const done = Promise.all(writes.map((w) => w.done)).then((os) => os.find((o) => o.type === "refused") ?? os.at(-1) ?? null);
			return { reply: r.reply as T, writes, done };
		},
		setPerson: (person) => call({ in: "set_person", person }),
		read(query) {
			return new Promise((ok, fail) => {
				let sub: Subscription | null = null;
				let done = false;
				sub = kit.subscribe(query, (v) => {
					if (done || v.status === "loading") return;
					done = true;
					queueMicrotask(() => sub?.close());
					if (v.status === "ready") ok(v.rows);
					else fail(new KitError(v.error ?? { type: "protocol", message: "read failed" }));
				});
			});
		},
		ask<T>(a: Ask) {
			if (closed) return Promise.reject(new KitError({ type: "offline", message: "closed" }));
			// The answer may be in the drain of this very call (a local one): register before draining.
			return new Promise<T>((ok, fail) => {
				const settle = (e: Extract<Effect, { type: "answered" }>) => (e.error ? fail(new KitError(e.error)) : ok(e.data as T));
				call({ in: "ask", ask: a }, ({ ask: id }: { ask: number }) => {
					const now = earlyAnswers.get(id);
					if (now) {
						earlyAnswers.delete(id);
						settle(now);
					} else answers.set(id, settle);
				});
			});
		},
		mutate,
		create: (kind, fields, opts = {}) => mutate({ op: "create", kind, fields, ...opts }),
		update: (kind, id, patch) => mutate({ op: "update", kind, id, fields: patch }),
		setToken: (token) => call({ in: "set_token", token }),
		retry: () => call({ in: "retry" }),
		pull: () => call({ in: "pull" }),
		reset: () => call({ in: "reset" }),
		get status() {
			return status;
		},
		onStatus(cb) {
			statusCbs.add(cb);
			return () => statusCbs.delete(cb);
		},
		onChange(cb) {
			changeCbs.add(cb);
			return () => changeCbs.delete(cb);
		},
		onUnauthorized(cb) {
			unauthCbs.add(cb);
			return () => unauthCbs.delete(cb);
		},
		close() {
			hangUp();
			for (const t of timers) clearTimeout(t);
			closed = true;
			driver.close();
			if (typeof window !== "undefined" && typeof document !== "undefined") {
				document.removeEventListener("visibilitychange", wake);
				window.removeEventListener("online", online);
				window.removeEventListener("offline", offline);
			}
		},
	};
	call({ in: "start" });
	return kit;
}
